import styled from '@emotion/styled';
import Button, { ButtonProps, ButtonVariants } from '@features/ui-form/components/Button/Button';
import { LayoutModal, ModalProps } from '@features/ui-layout/components/Modal/LayoutModal';
import { useTranslation } from 'next-i18next';
import { useMemo, JSX } from 'react';

export enum ConfirmModalButton {
  BUTTON_YES,
  BUTTON_NO,
  BUTTON_CUSTOM,
}

const ButtonMapping = {
  [ConfirmModalButton.BUTTON_CUSTOM]: { label: 'modal.custom' },
  [ConfirmModalButton.BUTTON_YES]: {
    label: 'modal.validate',
    startDecorator: '🔍',
  },
  [ConfirmModalButton.BUTTON_NO]: {
    label: 'modal.cancel',
    variant: ButtonVariants.SECONDARY,
  },
} as const satisfies Record<ConfirmModalButton, ButtonProps>;

export type ConfirmModalProps = {
  buttons?: ConfirmModalButton[];
  title: string;
  message: string;
  onClick: (clickedButton?: ConfirmModalButton) => void;
};

export const ConfirmModal = ({
  open,
  title,
  message,
  onClick,
  buttons,
  onClose,
}: ConfirmModalProps & Pick<ModalProps, 'onClose' | 'open'>): JSX.Element => {
  const { t } = useTranslation('common');
  const finalButtons = useMemo(
    () => buttons ?? [ConfirmModalButton.BUTTON_YES, ConfirmModalButton.BUTTON_NO],
    [buttons],
  );

  const actions: ModalProps['actions'] = actionOnClose => (
    <>
      {finalButtons.map(btn => {
        const btnBase = ButtonMapping[btn];

        return (
          <Button
            {...btnBase}
            key={btn}
            label={btnBase.label && t(btnBase.label)}
            onClick={() => {
              onClick(btn);
              if (actionOnClose) {
                actionOnClose();
              }
            }}
          />
        );
      })}
    </>
  );

  const body: ModalProps['body'] = (
    <Body>
      {message.split('\n').map(line => (
        <p key={line}>{line}</p>
      ))}
    </Body>
  );

  return <LayoutModal body={body} actions={actions} title={title} onClose={onClose} open={open} />;
};

const Body = styled.div``;
